<template>
  <section class="account-picker" data-testid="account-picker" :aria-busy="isSelecting ? 'true' : 'false'">
    <h1 class="account-picker__title">Choose an account</h1>
    <p class="account-picker__subtitle" data-testid="account-picker-subtitle">{{ subtitle }}</p>

    <div class="account-picker__field">
      <label class="account-picker__label" :for="inputId">Search accounts</label>
      <input
        :id="inputId"
        v-model="searchText"
        class="account-picker__input"
        data-testid="account-picker-search"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded="true"
        autocomplete="off"
        :aria-controls="listId"
        :aria-activedescendant="activeOptionId"
        :placeholder="placeholder"
        @input="onSearchInput"
        @keydown="onKeydown"
      />
    </div>

    <p
      v-if="showResultCount"
      class="account-picker__result-count"
      data-testid="account-picker-result-count"
      role="status"
    >
      {{ resultCountText }}
    </p>

    <div v-if="showEmpty" class="account-picker__empty" data-testid="account-picker-empty" role="status">
      <p class="account-picker__empty-title">No accounts match "{{ submittedSearch }}"</p>
      <p class="account-picker__empty-text">
        Check the spelling or try a shorter name. If an account is missing, ask its administrator to
        invite you.
      </p>
    </div>

    <div v-show="options.length" :id="listId" role="listbox" aria-label="Accounts" class="account-picker__list">
      <div v-if="showRecent" role="group" aria-labelledby="account-picker-recent" class="account-picker__recent">
        <h2 id="account-picker-recent" class="account-picker__section">Recent</h2>
        <div
          v-for="(account, index) in recentAccounts"
          :id="optionId(index)"
          :key="`recent-${account.id}`"
          role="option"
          class="account-picker__row"
          data-testid="account-picker-recent-row"
          :class="{ 'account-picker__row--active': activeIndex === index }"
          :aria-selected="activeIndex === index ? 'true' : 'false'"
          @click="choose(account)"
        >
          <span class="account-picker__names">
            <span class="account-picker__name">{{ account.name }}</span>
            <span class="account-picker__meta">{{ metaLine(account, true) }}</span>
          </span>
          <span class="account-picker__chevron" aria-hidden="true">&rsaquo;</span>
        </div>
      </div>

      <div v-if="accounts.length" role="group" aria-labelledby="account-picker-all" class="account-picker__all">
        <h2 v-if="!isSearchActive" id="account-picker-all" class="account-picker__section">
          All accounts ({{ total }})
          <span class="account-picker__section-note">A to Z</span>
        </h2>
        <span v-else id="account-picker-all" class="account-picker__sr-only">Search results</span>
        <div ref="scrollRef" class="account-picker__scroll" data-testid="account-picker-scroll" @scroll="onScroll">
          <div
            v-for="(account, index) in accounts"
            :id="optionId(recentCount + index)"
            :key="`all-${account.id}`"
            role="option"
            class="account-picker__row"
            data-testid="account-picker-row"
            :class="{ 'account-picker__row--active': activeIndex === recentCount + index }"
            :aria-selected="activeIndex === recentCount + index ? 'true' : 'false'"
            @click="choose(account)"
          >
            <span class="account-picker__names">
              <span class="account-picker__name">{{ account.name }}</span>
              <span class="account-picker__meta">{{ metaLine(account, false) }}</span>
            </span>
            <span class="account-picker__chevron" aria-hidden="true">&rsaquo;</span>
          </div>
          <p v-if="isLoadingMore" class="account-picker__loading" data-testid="account-picker-loading" role="status">
            Loading accounts…
          </p>
        </div>
      </div>
    </div>

    <p v-if="isSelecting" class="account-picker__count-line" role="status">Opening account…</p>
    <p v-else-if="showCountLine" class="account-picker__count-line" data-testid="account-picker-count-line">
      Showing {{ accounts.length }} of {{ countLineTotal }}. Scroll to load more.
    </p>

    <ExButton
      data-testid="btn-logout"
      outline="outline-default"
      size="medium"
      :fullwidth="true"
      label="Log out"
      @onClick="emit('logout')"
    />
  </section>
</template>

<script>
let pickerUid = 0;
</script>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import ExButton from "@components/buttons/Button.vue";

const LOAD_MORE_THRESHOLD_PX = 50;

const props = defineProps({
  accounts: { type: Array, default: () => [] },
  recentAccounts: { type: Array, default: () => [] },
  totalCount: { type: Number, default: null },
  resultCount: { type: Number, default: null },
  hasMore: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
  isSelecting: { type: Boolean, default: false },
  debounceMs: { type: Number, default: 300 },
});
const emit = defineEmits(["select", "search", "load-more", "logout"]);

pickerUid += 1;
const inputId = `account-picker-input-${pickerUid}`;
const listId = `account-picker-list-${pickerUid}`;

const searchText = ref("");
const submittedSearch = ref("");
const activeIndex = ref(-1);
const scrollRef = ref(null);
let debounceTimer = null;
let loadMoreGate = false;

const isSearchActive = computed(() => submittedSearch.value !== "");
const showRecent = computed(() => !isSearchActive.value && props.recentAccounts.length > 0);
const recentCount = computed(() => (showRecent.value ? props.recentAccounts.length : 0));
const options = computed(() => [...(showRecent.value ? props.recentAccounts : []), ...props.accounts]);
const total = computed(() => props.totalCount ?? props.accounts.length);
const countLineTotal = computed(() =>
  isSearchActive.value ? props.resultCount ?? props.accounts.length : total.value
);
const placeholder = computed(() => `Search ${total.value} accounts by name`);
const subtitle = computed(() => {
  if (isSearchActive.value) return `${total.value} accounts`;
  return props.recentAccounts.length
    ? `You belong to ${total.value} accounts. Search, or pick a recent one.`
    : `You belong to ${total.value} accounts. Search to find one.`;
});
const showResultCount = computed(
  () => isSearchActive.value && !props.isLoadingMore && props.accounts.length > 0
);
const resultCountText = computed(
  () => `${props.resultCount ?? props.accounts.length} results for "${submittedSearch.value}"`
);
const showEmpty = computed(
  () => isSearchActive.value && !props.isLoadingMore && props.accounts.length === 0
);
const showCountLine = computed(() => props.hasMore && props.accounts.length > 0);
const activeOptionId = computed(() => (activeIndex.value >= 0 ? optionId(activeIndex.value) : null));

function optionId(index) {
  return `${listId}-option-${index}`;
}

function capitalize(value) {
  const text = String(value);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatLastUsed(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (date.toDateString() === new Date().toDateString()) return "last used today";
  return `last used ${date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
}

function metaLine(account, withLastUsed) {
  const roles = Array.isArray(account.role) ? account.role : account.role ? [account.role] : [];
  const parts = [roles.map(capitalize).join(", ")];
  if (withLastUsed && account.last_accessed_at) parts.push(formatLastUsed(account.last_accessed_at));
  return parts.filter(Boolean).join(" · ");
}

function cancelDebounce() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

function onSearchInput() {
  cancelDebounce();
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    submittedSearch.value = searchText.value.trim();
    activeIndex.value = -1;
    emit("search", submittedSearch.value);
  }, props.debounceMs);
}

function choose(account) {
  if (props.isSelecting || !account) return;
  emit("select", account);
}

function emitLoadMore() {
  if (!props.hasMore || props.isLoadingMore || loadMoreGate) return;
  loadMoreGate = true;
  emit("load-more");
}

function onScroll() {
  const el = scrollRef.value;
  if (!el || el.scrollHeight <= el.clientHeight) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_THRESHOLD_PX) emitLoadMore();
}

function scrollActiveIntoView() {
  nextTick(() => {
    const el = activeOptionId.value ? document.getElementById(activeOptionId.value) : null;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  });
}

function moveActive(step) {
  const last = options.value.length - 1;
  if (last < 0) return;
  activeIndex.value = Math.min(last, Math.max(0, activeIndex.value + step));
  if (activeIndex.value >= last - 2) emitLoadMore();
  scrollActiveIntoView();
}

function onKeydown(event) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    moveActive(1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    moveActive(-1);
  } else if (event.key === "Enter") {
    event.preventDefault();
    choose(options.value[activeIndex.value]);
  } else if (event.key === "Escape" && searchText.value) {
    searchText.value = "";
    onSearchInput();
  }
}

watch(
  () => props.isLoadingMore,
  (loading) => {
    if (!loading) loadMoreGate = false;
  }
);
watch(
  () => props.hasMore,
  (more) => {
    if (!more) loadMoreGate = false;
  }
);
watch(
  () => props.accounts.length,
  (length, previous) => {
    if (length < previous) activeIndex.value = -1;
  }
);

onBeforeUnmount(cancelDebounce);
</script>

<style scoped>
.account-picker {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  text-align: left;
}
.account-picker__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #101828;
}
.account-picker__subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}
.account-picker__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.account-picker__label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}
.account-picker__input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background-color: #ffffff;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
}
.account-picker__input::placeholder {
  color: #667085;
}
.account-picker__input:focus {
  outline: none;
  border-color: #d6bbfb;
  box-shadow: 0 0 0 4px rgba(158, 119, 237, 0.25);
}
.account-picker__result-count,
.account-picker__section {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #101828;
}
.account-picker__section-note {
  margin-left: 8px;
  font-weight: 400;
  color: #344054;
}
.account-picker__list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.account-picker__recent,
.account-picker__all {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.account-picker__recent {
  gap: 8px;
}
.account-picker__recent .account-picker__section {
  margin-bottom: 16px;
}
.account-picker__scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #eaecf0 transparent;
}
.account-picker__row {
  display: flex;
  flex: none;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  min-height: 62px;
  padding: 10px 16px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background-color: #ffffff;
  cursor: pointer;
}
.account-picker__row:hover,
.account-picker__row--active {
  border-color: #7f56d9;
  background-color: #f9fafb;
}
.account-picker__names {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.account-picker__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}
.account-picker__meta {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #344054;
}
.account-picker__chevron {
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: #7f56d9;
}
.account-picker__loading,
.account-picker__count-line {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: #344054;
}
.account-picker__empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.account-picker__empty-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}
.account-picker__empty-text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}
.account-picker__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
</style>
